import { DoorOpen, Sparkles, TreePine, UploadCloud, UserRound } from 'lucide-react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Avatar } from '../../design-system/components/Avatar/Avatar';
import Badge from '../../design-system/components/Badge/Badge';
import Button from '../../design-system/components/Button/Button';
import { useRoomInbox } from '../../features/rooms/RoomInboxContext.jsx';
import { getRoomsBasePath } from '../../service/constants';
import { protectedUrl } from '../../service/request';
import { primaryNavKeyFromPathname } from './headerRoute';
import styles from './MobilePrimaryNav.module.css';

const navItems = [
  {
    key: 'my-light',
    messageId: 'my_light',
    defaultMessage: 'My Light',
    href: '/members/me',
    Icon: UserRound,
  },
  {
    key: 'home',
    messageId: 'nav.sparks',
    defaultMessage: 'Create Spark',
    href: '/sparks',
    Icon: Sparkles,
  },
  {
    key: 'groups',
    messageId: 'nav.groups',
    defaultMessage: 'Rooms',
    href: getRoomsBasePath(),
    Icon: DoorOpen,
  },
  {
    key: 'graves',
    messageId: 'nav.graves',
    mobileMessageId: 'nav.mobile_graves',
    defaultMessage: 'My graves',
    href: '/graves',
    Icon: UploadCloud,
  },
  {
    key: 'family-tree',
    messageId: 'nav.my_family',
    defaultMessage: 'Family tree',
    href: '/family-tree',
    Icon: TreePine,
  },
];

const buildFamilyTreePath = (userId) => {
  if (!userId) return '/family-tree';

  const rootId = `u_${userId}`;
  try {
    localStorage.setItem('familyTree.lastId', rootId);
  } catch {
    // Navigation must still work when storage is unavailable.
  }

  const params = new URLSearchParams({
    radius: '3',
    tab: 'tree',
    label_strategy: 'kinship',
    viewer_person_id: rootId,
  });
  return `/family-tree/${encodeURIComponent(rootId)}?${params.toString()}`;
};

export const MobilePrimaryNav = () => {
  const intl = useIntl();
  const location = useLocation();
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state?.user?.user || state?.user);
  const userId = currentUser?.id;
  const { totalUnreadCount } = useRoomInbox();
  const profilePath = currentUser?.slug ? `/members/${currentUser.slug}` : '/members/me';
  const activeKey =
    location.pathname === profilePath || location.pathname === '/members/me'
      ? 'my-light'
      : primaryNavKeyFromPathname(location.pathname);

  const handleNavigate = (item) => {
    const target =
      item.key === 'family-tree'
        ? buildFamilyTreePath(userId)
        : item.key === 'my-light'
          ? profilePath
          : item.href;
    navigate(target);
  };

  return (
    <nav
      className={styles.nav}
      aria-label={intl.formatMessage({
        id: 'nav.primary',
        defaultMessage: 'Primary navigation',
      })}
    >
      <div className={styles.items}>
        {navItems.map(({ Icon, ...item }) => {
          const label = intl.formatMessage({
            id: item.messageId,
            defaultMessage: item.defaultMessage,
          });
          const visibleLabel = item.mobileMessageId
            ? intl.formatMessage({ id: item.mobileMessageId, defaultMessage: label })
            : label;
          const active = activeKey === item.key;
          const baseIcon =
            item.key === 'my-light' ? (
              <Avatar
                size={23}
                src={currentUser?.avatar ? protectedUrl(currentUser.avatar) : undefined}
                icon={<UserRound size={16} aria-hidden />}
                className={styles.profileAvatar}
              />
            ) : (
              <Icon size={21} strokeWidth={active ? 2.4 : 2} aria-hidden />
            );
          const icon =
            item.key === 'groups' ? (
              <Badge
                count={totalUnreadCount}
                overflowCount={99}
                size="small"
                tone="brand"
                offset={[2, 3]}
                className={styles.roomUnreadBadge}
                slotProps={{ indicator: { className: styles.roomUnreadIndicator } }}
              >
                {baseIcon}
              </Badge>
            ) : (
              baseIcon
            );
          const ariaLabel =
            item.key === 'groups' && totalUnreadCount > 0
              ? intl.formatMessage(
                  {
                    id: 'rooms.navigation.unread_label',
                    defaultMessage:
                      'Rooms, {count, plural, one {# unread message} other {# unread messages}}',
                  },
                  { count: totalUnreadCount },
                )
              : label;
          return (
            <Button
              key={item.key}
              variant="text"
              className={`${styles.item} ${active ? styles.active : ''}`}
              aria-current={active ? 'page' : undefined}
              aria-label={ariaLabel}
              onClick={() => handleNavigate(item)}
              classNames={{ content: styles.itemContent }}
            >
              {icon}
              <span className={styles.itemLabel}>{visibleLabel}</span>
            </Button>
          );
        })}
      </div>
    </nav>
  );
};

export default MobilePrimaryNav;
